import React, { useEffect, useState, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import esLocale from "@fullcalendar/core/locales/fr";
import { IntlProvider, FormattedMessage } from "react-intl";
import messages_en from "./translations/en.json";
import messages_fr from "./translations/fr.json";
const messages = {
    en: messages_en,
    fr: messages_fr,
};
import interactionPlugin from "@fullcalendar/interaction";
import { useNavigate } from "react-router-dom";
import { useNetwork } from "../../components/NetworkProvider";
import { useOfflineMeetings } from "../OfflineContext/index.jsx";

import SharedCalendarList from "./SharedCalendarList";
import { FiRefreshCcw } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import Throbber from "../../style/Throbber";
import CalendarViewToggle from "../Buttons/CalendarViewToggle";
import MeetingModal from "../ModalComponents/MeetingModal";
import ReportPopUp from "../ModalComponents/ReportPopUp/index.jsx";
import { Button } from "@mui/material";
import { HiCalendarDays } from "react-icons/hi2";

import {
    blankMeetingData,
    formatSingleEvent,
    formatNewMeeting,
    formatExistingMeeting,
    formatMeetingToToday,
    formatToTimestamp,
} from "../../utils/Meetings/functions";
import { formatReports, toggleReport } from "../../utils/Reports/functions";

import FastAPIClient from "../../client";
import fast_api_config from "../../config";

const client = new FastAPIClient(fast_api_config);

const CalendarModule = ({
    meetings,
    height = "80vh",
    locale,
    detailsTrue,
    setDetailsTrue,
    originalMeetings,
    setOriginalMeetings,
    meetingTypes,
    activeCalendars,
    setActiveCalendars,
    calendarLoading,
    setCalendarLoading,
    usersData,
    addNewEvent,
    setMeetingUpdated,
    handleDeleteEvent,
    successMessage,
    errorMessage,
    setSuccessMessage,
    setErrorMessage,
}) => {
    const user = JSON.parse(localStorage.getItem("user"));
    const { isOnline } = useNetwork();
    const { offlineMeetings, offlineReports, offlineUpdateMeeting } =
        useOfflineMeetings();
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [usersList, setUsersList] = useState([]);
    const [usersListOriginal, setUsersListOriginal] = useState([]);

    // Modal related state
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [newEventData, setNewEventData] = useState(blankMeetingData);
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [reportsData, setReportsData] = useState([]);
    const [reportLayout, setReportLayout] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);

    useEffect(() => {
        fetchReportsData();
    }, []);
    const fetchReportsData = async () => {
        if (!calendarLoading) {
            setCalendarLoading(true);
        }
        try {
            const data = await client.getReports(null, null);
            setReportsData(data.results);
            const reportsLayoutData = await client.getReportLayout();
            setReportLayout(reportsLayoutData);
        } catch (error) {
            console.log(error);
        }
        if (meetings.length > 0 && !calendarLoading) {
            setCalendarLoading(false);
        }
    };

    // useMemo to track reports made offline
    const total_reports = useMemo(() => {
        const formatList = formatReports(offlineReports, user);

        let baseReports = isOnline ? reportsData : [];

        // Merge online and offline when online, or just use offline when offline
        let merged = baseReports.concat(offlineMeetings, formatList);

        // Apply updates
        if (offlineUpdateMeeting.length) {
            merged = merged.map((meeting) => {
                const updatedMeeting = offlineUpdateMeeting.find(
                    (updated) =>
                        updated.meeting_id === meeting.meeting_id &&
                        updated.client?.client_id === meeting.client_id
                );
                return updatedMeeting
                    ? { ...meeting, ...updatedMeeting }
                    : meeting;
            });
        }

        // Sort by start date
        return merged.sort(
            (a, b) => new Date(a.start_date) - new Date(b.start_date)
        );
    }, [
        isOnline,
        user,
        reportsData,
        offlineReports,
        offlineMeetings,
        offlineUpdateMeeting,
    ]);

    useEffect(() => {
        setCalendarLoading(true);
        const formatCalendarEvents = (list) => {
            return list.map((item) => formatSingleEvent(item));
        };
        setCalendarEvents(formatCalendarEvents(meetings));
    }, [meetings]);

    useEffect(() => {
        setCalendarLoading(false);
    }, [calendarEvents]);

    const [viewMode, setViewMode] = useState("weekly"); // new state for switching views
    // const location = useLocation(); // Hook to get the current URL
    const navigate = useNavigate(); // Hook to update the URL

    const handleViewToggle = () => {
        const newViewMode = viewMode === "daily" ? "weekly" : "daily";
        setViewMode(newViewMode);
        navigate(`?view=${newViewMode}`); // Update URL with the new view mode
    };

    const validateDrop = (event) => {
        const newStart = event.start;
        const newEnd = event.end;

        // Create Date objects for working hours on the same day as the event
        const workingHoursStart = new Date(newStart);
        workingHoursStart.setHours(8, 0, 0, 0); // 08:00 AM

        const workingHoursEnd = new Date(newStart);
        workingHoursEnd.setHours(20, 0, 0, 0); // 08:00 PM

        // Check if the event falls within working hours
        return newStart >= workingHoursStart && newEnd <= workingHoursEnd;
    };

    const handleEventDrop = async (info) => {
        const { event, revert } = info;
        const meetingData = event.extendedProps;

        if (
            !meetingData.attendees.includes(user.user_id) ||
            meetingData.user_id != user.user_id ||
            meetingData.is_visited
        ) {
            revert();
            return;
        }

        if (!validateDrop(event)) {
            revert();
            setErrorMessage("calendar.hours_error");
            return;
        }

        const newStart = event.start;
        const newEnd = event.end;
        let offline_params = {};
        if (!isOnline) {
            const description = meetingTypes.find(
                (item) => item.meeting_type_id === meetingData.meeting_type_id
            )?.description;
            const updateTimeData = detailsTrue
                ? {
                      updated_at: new Date().toISOString(),
                  }
                : {
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                  };
            offline_params = {
                user: {
                    user_id: user?.user_id,
                    first_name: user?.first_name,
                    last_name: user?.last_name,
                },
                type: {
                    description: description,
                },
                client: meetingData.client,
                ...updateTimeData,
            };
        }

        try {
            setIsUpdating(true);
            const payload = {
                meeting_id: meetingData.meeting_id,
                title: event.title,
                client_id: meetingData.client.client_id,
                user_id: user.user_id,
                user: user,
                start_date: formatToTimestamp(newStart),
                end_date: formatToTimestamp(newEnd),
                updated_at: new Date().toISOString(),
                meeting_type_id: meetingData.meeting_type_id,
                attendees: meetingData.attendees,
                client: meetingData.client,
                description: meetingData.description,
                lat: meetingData.lat,
                long: meetingData.long,
                address_1: meetingData.address_1,
                zip_code: meetingData.zip_code,
                city: meetingData.city,
                country: meetingData.country,
                is_organizer: meetingData.is_organizer,
                is_visited: meetingData.is_visited,
                ...offline_params,
            };
            await client.updateMeeting(meetingData.meeting_id, payload);

            // Directly update event without causing full rerender
            event.setDates(newStart, newEnd);

            setSuccessMessage("update.success");

            // Optional: only call setMeetingUpdated if you know it won't override FullCalendar events
            setMeetingUpdated({ ...payload, is_recurring: false });
        } catch (err) {
            console.log(err);
            setErrorMessage("submit.error");
            revert();
        }
        setIsUpdating(false);
    };

    const openModal = (date) => {
        if (date) {
            setNewEventData(formatNewMeeting(date, user));
        } else {
            const currentDate = new Date();
            formatMeetingToToday(
                currentDate,
                setNewEventData,
                user,
                setDetailsTrue
            );
        }

        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Function to handle adding an event when a time slot is clicked
    const handleDateClick = (arg) => {
        setDetailsTrue(false);
        openModal(arg.date);
    };

    // Function to handle event navigation
    const handleEventClick = (eventInfo) => {
        const allEventInfo = {
            ...eventInfo.event.extendedProps,
            title: eventInfo.event.title,
            start: eventInfo.event.start,
            end: eventInfo.event.end,
        };
        setNewEventData(formatExistingMeeting(allEventInfo));

        if (!eventInfo.event.extendedProps.is_visited) {
            if (eventInfo.event.extendedProps.user_id === user.user_id) {
                setDetailsTrue(true);
            }
            setIsModalOpen(true);
        } else {
            const report = total_reports.find(
                (report) =>
                    report.Report?.meeting_id ===
                    eventInfo.event.extendedProps.meeting_id
            );
            if (!report) {
                return;
            }
            // Pass the Report object directly since that's what DisplayReport expects
            setSelectedReport(report.Report);
            setIsReportOpen(true);
        }
    };
    // update existing event : drag and drop
    const updateExistingEvent = (updatedMeeting) => {
        setMeetingUpdated(updatedMeeting);
    };

    const getEventTitle = (eventInfo) => {
        const title = eventInfo.event.title;
        // Consider "null", undefined, and empty title as invalid
        const isValidTitle = title && title !== "null" && title !== "undefined";

        // Convert to title case if the title is all uppercase
        const toTitleCase = (str) => {
            return str
                .toLowerCase() // First, convert the entire string to lowercase
                .replace(/\b\w/g, (match) => match.toUpperCase()); // Capitalize the first letter of each word
        };

        const titleInTitleCase =
            title && title === title.toUpperCase() ? toTitleCase(title) : title;

        // Use the title if valid, otherwise fall back to no title scenario
        const noTitleText = messages[locale]["calendar.noTitle"];
        const validTitle = isValidTitle ? titleInTitleCase : noTitleText;

        // Truncate if the length is greater than 20
        return validTitle.length > 20
            ? `${validTitle.substring(0, 15)}...`
            : validTitle;
    };

    const renderEventContent = (eventInfo) => {
        const isAttendee =
            eventInfo.event.extendedProps.user_id != user.user_id;
        let borderStyle = {};
        // Check if user is not an attendee
        if (
            isAttendee ||
            eventInfo.event.extendedProps.user_id != user.user_id
        ) {
            const matchingUser = usersListOriginal.find(
                (el) =>
                    el.user.user_id ===
                    eventInfo.event.extendedProps.user.user_id
            );

            if (matchingUser?.randomColor) {
                borderStyle = {
                    backgroundColor: "white",
                    color: `${matchingUser.randomColor}`,
                    borderColor: `${matchingUser.randomColor}`, // Apply border and color together
                };
            }
        }
        // Get the meeting type ID from extendedProps
        const meetingTypeId = eventInfo.event.extendedProps.type;

        // Find the corresponding meeting type in meetingTypes array
        const meetingType = meetingTypes.find(
            (type) => String(type.meeting_type_id) === String(meetingTypeId)
        );

        // Get the color associated with the meeting type, or use a default
        const bgColor = `${
            isAttendee
                ? ""
                : eventInfo.event.extendedProps.is_visited
                ? "#6D82A0"
                : meetingType?.color
        }`;

        // Base styles if no border color is set
        const baseStyle = {
            display: "flex",
            border: "solid",
            cursor: "pointer",
            borderWidth: "1.5px",
            width: "100%",
            borderRadius: 6,
            fontSize: "small",
            fontWeight: "bold",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: bgColor,
            ...borderStyle, // Spread in any border styles
        };
        return (
            <div style={baseStyle} className="bg-transparent h-full w-full">
                <div className="pl-2 pr-1 w-full flex justify-between">
                    <div className="w-full fc-event-title">
                        {getEventTitle(eventInfo)}
                    </div>

                    <div>
                        {eventInfo.event.extendedProps.is_recurring && (
                            <FiRefreshCcw />
                        )}
                        {eventInfo.event.extendedProps.meeting_id.slice(
                            0,
                            2
                        ) === "AI" && (
                            <div className="flex items-center justify-center h-full w-full">
                                <span
                                    className="text-xxs font-semibold"
                                    style={{
                                        padding: "1px",
                                        paddingLeft: "1.5px",
                                        paddingRight: "1.5px",
                                    }}
                                >
                                    <HiSparkles />
                                </span>
                                {/* <span className="text-white text-xxs font-bold pr-1">
                                    IA
                                </span> */}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // const renderEventClassNames = (eventInfo) => {
    //     const isAttendee = eventInfo.event.extendedProps.is_organizer === false;

    //     // If the meeting has been visited, return gray background color
    //     if (eventInfo.event.extendedProps.is_visited) {
    //         return isAttendee ? "bg-gray-200" : "bg-gray-200";
    //     }
    //     if (
    //         eventInfo.event.extendedProps.user &&
    //         eventInfo.event.extendedProps.user.user_id != user.user_id
    //     ) {
    //         return ` bg-gray-200`;
    //     }

    //     // Check if meetingTypes is ready
    //     if (!Array.isArray(meetingTypes) || meetingTypes.length === 0) {
    //         return "bg-rose-500"; // Default color when meetingTypes is not available
    //     }

    //     // If it's 'attendee' mode, return a different background color
    //     if (isAttendee) {
    //         return `bg-gray-100`;
    //     }

    //     let baseClass = "";

    //     return baseClass; // Return the dynamic background color
    // };

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(""), 3000); // Hide message after 3 seconds
            return () => clearTimeout(timer); // Cleanup on unmount
        }
    }, [successMessage]);
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(""), 3000); // Hide message after 3 seconds
            return () => clearTimeout(timer); // Cleanup on unmount
        }
    }, [errorMessage]);

    // Calculate current time for scrolling
    const getCurrentScrollTime = () => {
        const now = new Date();
        // Subtract 1 hour from current time
        now.setHours(now.getHours() - 1);
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        return `${hours}:${minutes}:00`;
    };

    return (
        <IntlProvider
            locale={locale}
            defaultLocale="en"
            messages={messages[locale]}
        >
            {meetingTypes && (
                <div className="flex flex-col w-full mt-10 relative h-5/6">
                    {isUpdating && (
                        <div className="absolute top-1/2 left-1/2 z-10">
                            <Throbber />
                        </div>
                    )}

                    <div className="flex flex-row w-full relative h-5/6">
                        {calendarLoading && (
                            <div className="absolute top-1/2 left-1/2 z-10">
                                <Throbber />
                            </div>
                        )}

                        <div className="flex flex-col">
                            <div className="flex w-full justify-start items-center mb-5">
                                {" "}
                                <CalendarViewToggle
                                    viewMode={viewMode}
                                    onChange={handleViewToggle}
                                />
                            </div>

                            <div className="">
                                <Button
                                    onClick={() => openModal(null)}
                                    variant="contained"
                                    color="primary"
                                    startIcon={<HiCalendarDays />}
                                >
                                    <FormattedMessage id="button.newMeeting" />
                                </Button>
                            </div>

                            <SharedCalendarList
                                currentUser={user}
                                activeCalendars={activeCalendars}
                                setActiveCalendars={setActiveCalendars}
                                originalMeetings={originalMeetings}
                                setOriginalMeetings={setOriginalMeetings}
                                setCalendarLoading={setCalendarLoading}
                                usersList={usersList}
                                setUsersList={setUsersList}
                                setUsersListOriginal={setUsersListOriginal}
                                calendarLoading={calendarLoading}
                                isOnline={isOnline}
                            />
                        </div>

                        <div className="w-full h-full">
                            <FullCalendar
                                locale={esLocale}
                                slotMinTime={"06:00:00"}
                                slotMaxTime={"23:00:00"}
                                plugins={[timeGridPlugin, interactionPlugin]}
                                initialView="timeGridWeek"
                                events={calendarEvents.map((event) => ({
                                    ...event,
                                    startEditable:
                                        event.attendees?.includes(
                                            user.user_id
                                        ) &&
                                        event.user_id === user.user_id &&
                                        !event.is_visited,
                                    durationEditable: false,
                                    resourceEditable: false,
                                }))}
                                editable={true}
                                eventStartEditable={(event) => {
                                    return event.extendedProps.attendees?.includes(
                                        user.user_id
                                    );
                                }}
                                eventDurationEditable={false} // Disables event resizing
                                eventResizableFromStart={false}
                                eventDrop={handleEventDrop}
                                height={height}
                                dateClick={handleDateClick}
                                eventClick={handleEventClick}
                                eventContent={renderEventContent}
                                allDaySlot={false} // Removes the "All Day" column
                                dayHeaderFormat={{
                                    weekday: "long",
                                    day: "numeric",
                                }} // Removes the month, showing only day name and date
                                dayHeaderContent={(arg) => {
                                    const isToday =
                                        arg.date.toDateString() ===
                                        new Date().toDateString();
                                    const capitalizeFirstLetter = (string) =>
                                        string.charAt(0).toUpperCase() +
                                        string.slice(1).toLowerCase();

                                    const weekday = capitalizeFirstLetter(
                                        arg.text.split(" ")[0]
                                    );
                                    const date = arg.text.split(" ")[1];

                                    return (
                                        <div className="custom-day-header mb-3 font-light">
                                            {isToday ? (
                                                <>
                                                    <div className="day text-sm mb-2">
                                                        {weekday}
                                                    </div>
                                                    <div className="bg-gold rounded-full text-black text-base font-semibold">
                                                        <span>{date}</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="day text-sm mb-2">
                                                        {weekday}
                                                    </div>
                                                    <div className="date text-base font-semibold">
                                                        {date}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                }}
                                slotDuration="00:30:00"
                                slotLabelInterval="01:00:00" // Only shows labels every hour
                                titleFormat={(date) => {
                                    const dateStr = date.start.marker;
                                    const jsDate = new Date(dateStr);
                                    const frenchMonths = [
                                        "Janvier",
                                        "Février",
                                        "Mars",
                                        "Avril",
                                        "Mai",
                                        "Juin",
                                        "Juillet",
                                        "Août",
                                        "Septembre",
                                        "Octobre",
                                        "Novembre",
                                        "Décembre",
                                    ];
                                    const monthIndex = jsDate.getMonth();
                                    const monthName = frenchMonths[monthIndex];
                                    const year = jsDate.getFullYear();
                                    return `${monthName} ${year}`;
                                }}
                                headerToolbar={{
                                    left: "today prev,next", // Replace "today" with "customToday"
                                    center: "title",
                                    right: "",
                                }}
                                scrollTime={getCurrentScrollTime()}
                                scrollTimeReset={false}
                            />
                        </div>
                    </div>
                    {isModalOpen && (
                        <MeetingModal
                            attendees={usersData}
                            isModalOpen={isModalOpen}
                            closeModal={closeModal}
                            initialEventData={newEventData}
                            locale={locale}
                            onCreateMeeting={addNewEvent}
                            onUpdateMeeting={updateExistingEvent}
                            onDeleteMeeting={handleDeleteEvent}
                            user_id={user.user_id}
                            detailsTrue={detailsTrue}
                            meetings={meetings}
                            user={user}
                            setSuccessMessage={setSuccessMessage}
                            setErrorMessage={setErrorMessage}
                        />
                    )}
                    {isReportOpen && (
                        <ReportPopUp
                            togglePopup={() =>
                                toggleReport(null, setIsReportOpen)
                            }
                            reportData={selectedReport}
                            reportLayout={reportLayout}
                        />
                    )}
                    {successMessage && (
                        <div className="fixed bottom-0 left-0 right-0 bg-green-500 text-white text-center py-2 z-50 mx-auto w-1/5 rounded-t-lg">
                            {successMessage}
                        </div>
                    )}
                    {errorMessage && (
                        <div className="fixed bottom-0 left-0 right-0 bg-rose-500 text-white text-center py-2 z-50 mx-auto w-1/5 rounded-t-lg">
                            {errorMessage}
                        </div>
                    )}
                </div>
            )}
        </IntlProvider>
    );
};

export default CalendarModule;
